<script setup lang="ts">
import {
  PAYOUT_METHODS,
  type PayoutDetails,
  type PayoutDetailsField,
  validatePayoutDetails,
} from '~/constants/affiliate'
import { countryOptions } from '~/constants/countries'

/**
 * What a partner must give before the first payout (section 6 of the affiliate
 * terms): who they are, their address and tax details, and where the money
 * goes. Validated with the same rules the server applies.
 */
const props = defineProps<{
  details: PayoutDetails | null
}>()

const emit = defineEmits<{
  saved: []
}>()

const { t, locale } = useI18n()
const { show: showSnackbar } = useSnackbar()

const empty = (): PayoutDetails => ({
  entityType: 'individual',
  legalName: '',
  street: '',
  city: '',
  postalCode: '',
  country: '',
  companyId: '',
  vatId: '',
  method: 'paypal',
  payoutEmail: '',
  iban: '',
  swift: '',
})

const form = reactive<PayoutDetails>(empty())
watch(() => props.details, (details) => {
  Object.assign(form, empty(), details ?? {})
}, { immediate: true })

const countries = computed(() => countryOptions(locale.value))
const errors = ref<PayoutDetailsField[]>([])
const touched = ref(false)
const saving = ref(false)

const fieldError = (field: PayoutDetailsField) =>
  touched.value && errors.value.includes(field) ? t(`affiliate.payout.errors.${field}`) : undefined

watch(form, () => {
  if (touched.value) errors.value = validatePayoutDetails(form).errors
})

async function save() {
  touched.value = true
  errors.value = validatePayoutDetails(form).errors
  if (errors.value.length) return

  saving.value = true
  try {
    await $fetch('/api/affiliate/payout-details', { method: 'PUT', body: form })
    showSnackbar(t('affiliate.payout.saved'))
    emit('saved')
  } catch (err: unknown) {
    const fields = (err as { data?: { data?: { fields?: PayoutDetailsField[] } } })?.data?.data?.fields
    if (fields?.length) errors.value = fields
    else showSnackbar(t('affiliate.payout.saveError'))
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <form class="space-y-5" novalidate @submit.prevent="save">
    <fieldset>
      <legend class="field-label">{{ t('affiliate.payout.entityType') }}</legend>
      <div class="mt-2 flex flex-wrap gap-6">
        <label v-for="type in ['individual', 'business'] as const" :key="type" class="flex cursor-pointer items-center gap-2 text-sm">
          <input v-model="form.entityType" type="radio" name="entityType" :value="type" class="size-4 accent-accent">
          {{ t(`affiliate.payout.entity.${type}`) }}
        </label>
      </div>
    </fieldset>

    <SofaxInput
      v-model="form.legalName"
      :label="t(form.entityType === 'business' ? 'affiliate.payout.companyName' : 'affiliate.payout.legalName')"
      :error="fieldError('legalName')"
      autocomplete="name"
      required
    />

    <div v-if="form.entityType === 'business'" class="grid gap-5 sm:grid-cols-2">
      <SofaxInput v-model="form.companyId" :label="t('affiliate.payout.companyId')" :error="fieldError('companyId')" required />
      <SofaxInput v-model="form.vatId" :label="t('affiliate.payout.vatId')" :hint="t('affiliate.payout.vatIdHint')" :error="fieldError('vatId')" />
    </div>

    <SofaxInput v-model="form.street" :label="t('affiliate.payout.street')" :error="fieldError('street')" autocomplete="street-address" required />
    <div class="grid gap-5 sm:grid-cols-3">
      <SofaxInput v-model="form.city" class="sm:col-span-2" :label="t('affiliate.payout.city')" :error="fieldError('city')" autocomplete="address-level2" required />
      <SofaxInput v-model="form.postalCode" :label="t('affiliate.payout.postalCode')" :error="fieldError('postalCode')" autocomplete="postal-code" required />
    </div>
    <SofaxSelect v-model="form.country" :label="t('affiliate.payout.country')" :hint="t('affiliate.payout.countryHint')" :error="fieldError('country')" required>
      <option value="" disabled>{{ t('affiliate.payout.chooseCountry') }}</option>
      <option v-for="country in countries" :key="country.code" :value="country.code">{{ country.name }}</option>
    </SofaxSelect>

    <fieldset>
      <legend class="field-label">{{ t('affiliate.payout.method') }}</legend>
      <div class="mt-2 flex flex-wrap gap-6">
        <label v-for="method in PAYOUT_METHODS" :key="method" class="flex cursor-pointer items-center gap-2 text-sm">
          <input v-model="form.method" type="radio" name="method" :value="method" class="size-4 accent-accent">
          {{ t(`affiliate.payout.methods.${method}`) }}
        </label>
      </div>
    </fieldset>

    <SofaxInput
      v-if="form.method !== 'bank'"
      v-model="form.payoutEmail"
      type="email"
      :label="t(`affiliate.payout.accountEmail.${form.method}`)"
      :error="fieldError('payoutEmail')"
      autocomplete="email"
      required
    />
    <div v-else class="grid gap-5 sm:grid-cols-3">
      <SofaxInput v-model="form.iban" class="sm:col-span-2" :label="t('affiliate.payout.iban')" :error="fieldError('iban')" required />
      <SofaxInput v-model="form.swift" :label="t('affiliate.payout.swift')" :error="fieldError('swift')" required />
    </div>

    <p class="text-xs text-muted dark:text-gray-400">
      {{ t('affiliate.payout.privacy') }}
      <LocaleLink to="/affiliate/terms#payout-details" class="link">{{ t('affiliate.payout.whyLink') }}</LocaleLink>
    </p>

    <SofaxBtn type="submit" :label="saving ? t('affiliate.payout.saving') : t('affiliate.payout.save')" :disabled="saving" />
  </form>
</template>
